import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import Sidebar from './Sidebar';
import { HeartLogo } from './hbs/HeartLogo';
import { TrialBanner } from './TrialBanner';
import { FreeAccountBanner } from './FreeAccountBanner';
import { LapsedMemberBanner } from './LapsedMemberBanner';
import { TransientBanner } from './TransientBanner';
import { PendingFinalizationsBanner } from './PendingFinalizationsBanner';
import { WelcomeBanner } from './WelcomeBanner';
import { AccountSetupBanner } from './AccountSetupBanner';
import { SupportChat } from './SupportChat';

const NO_SHELL_ROUTES = ['/auth'];
const MOBILE_BREAKPOINT = 600;

interface Props {
  children: React.ReactNode;
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function MobileBanner(): JSX.Element {
  return (
    <div className="min-h-screen bg-hbs-bg flex flex-col items-center justify-center p-6 text-center gap-4">
      <HeartLogo size={72} />
      <h1 className="font-display text-[28px] tracking-hbs-tight text-hbs-text">
        Hey Be Safe
      </h1>
      <p className="text-[14px] text-hbs-text-mute max-w-xs">
        The web app is designed for tablet and desktop. For phone-width access, install the Hey Be Safe app.
      </p>
    </div>
  );
}

export default function AppShell({ children }: Props): JSX.Element {
  const { pathname } = useLocation();
  const { status } = useSafe();
  const isMobile = useIsMobile();

  const showShell = !NO_SHELL_ROUTES.includes(pathname) && status !== 'unauthenticated';

  if (!showShell) {
    return (
      <div className="min-h-screen bg-hbs-bg flex items-center justify-center">
        {children}
      </div>
    );
  }

  if (isMobile) {
    return <MobileBanner />;
  }

  return (
    <div className="flex flex-col h-screen bg-hbs-bg overflow-hidden">
      <AccountSetupBanner />
      <TransientBanner />
      <PendingFinalizationsBanner />
      <TrialBanner />
      <FreeAccountBanner />
      <LapsedMemberBanner />
      <WelcomeBanner />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
      {/* Help assistant is only available on an unlocked safe — unmounting
          when locked hides the launcher and closes any open panel. */}
      {status === 'unlocked' && <SupportChat />}
    </div>
  );
}
